import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface NavbarProps {
  scrolled?: boolean;
}

export default function Navbar({ scrolled: propScrolled }: NavbarProps) {
  const [scrolled, setScrolled] = useState(propScrolled || false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    if (propScrolled !== undefined) return;
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [propScrolled]);

  const LogoComponent = () => (
    <img src="https://static.readdy.ai/image/ecd9e0a88a19846633f9ef23a8543c57/a22502e2a1fd52e546dc9b0190a6b81c.jpeg" alt="SEOgenia - Automatización SEO con IA Generativa" className="h-16 w-auto" />
  );

  const handleContactClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    
    if (isHomePage) {
      const contactSection = document.getElementById('contacto');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/#contacto');
      setTimeout(() => {
        const contactSection = document.getElementById('contacto');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-slate-900 shadow-md' : 'bg-slate-900 shadow-sm'}`}>
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {isHomePage ? (
            <h1 className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-3">
                <LogoComponent />
              </Link>
            </h1>
          ) : (
            <Link to="/" className="flex items-center gap-3">
              <LogoComponent />
            </Link>
          )}
          
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-white hover:text-gray-200 transition-colors font-medium cursor-pointer">
              Inicio
            </Link>
            <Link to="/como-lo-hacemos" className="text-white hover:text-gray-200 transition-colors font-medium cursor-pointer">
              Cómo lo Hacemos
            </Link>
            <div 
              className="relative group"
              onMouseEnter={() => setServicesDropdownOpen(true)}
              onMouseLeave={() => setServicesDropdownOpen(false)}
            >
              <button className="text-white hover:text-gray-200 transition-colors font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer">
                Servicios
                <i className="ri-arrow-down-s-line"></i>
              </button>
              
              {servicesDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 py-2 animate-fade-in">
                  <Link 
                    to="/servicios/arquitectura-semantica" 
                    className="block px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-all duration-300 text-sm font-medium cursor-pointer"
                  >
                    <i className="ri-code-box-line mr-2"></i>
                    Arquitectura Semántica
                  </Link>
                  <Link 
                    to="/servicios/schema-markup-avanzado" 
                    className="block px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-all duration-300 text-sm font-medium cursor-pointer"
                  >
                    <i className="ri-file-code-line mr-2"></i>
                    Schema Markup Avanzado
                  </Link>
                  <Link 
                    to="/servicios/seo-agentes-ia" 
                    className="block px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-all duration-300 text-sm font-medium cursor-pointer"
                  >
                    <i className="ri-robot-line mr-2"></i>
                    SEO para Agentes IA
                  </Link>
                  <Link 
                    to="/servicios/voice-search-seo" 
                    className="block px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-all duration-300 text-sm font-medium cursor-pointer"
                  >
                    <i className="ri-mic-line mr-2"></i>
                    Voice Search SEO
                  </Link>
                  <Link 
                    to="/servicios/progressive-web-app" 
                    className="block px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-all duration-300 text-sm font-medium cursor-pointer"
                  >
                    <i className="ri-smartphone-line mr-2"></i>
                    Progressive Web App
                  </Link>
                  <Link 
                    to="/servicios/optimizacion-extrema" 
                    className="block px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-all duration-300 text-sm font-medium cursor-pointer"
                  >
                    <i className="ri-rocket-line mr-2"></i>
                    Optimización Extrema
                  </Link>
                </div>
              )}
            </div>
            
            <a href="#contacto" onClick={handleContactClick} className="px-6 py-3 bg-cyan-500 text-white rounded-lg font-medium hover:bg-cyan-600 hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer">Contactar</a>
          </div>

          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-white"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-gray-700">
            <div className="flex flex-col gap-4 pt-4">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="font-medium text-white hover:text-gray-200 transition-colors">Inicio</Link>
              <Link to="/como-lo-hacemos" onClick={() => setMobileMenuOpen(false)} className="font-medium text-white hover:text-gray-200 transition-colors">Cómo lo Hacemos</Link>
              
              {/* Mobile Services Submenu */}
              <div>
                <button 
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className="font-medium text-white hover:text-gray-200 transition-colors flex items-center justify-between w-full cursor-pointer"
                >
                  Servicios
                  <i className={`ri-arrow-down-s-line transition-transform duration-300 ${mobileServicesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileServicesOpen && (
                  <div className="ml-4 mt-2 space-y-2">
                    <Link to="/servicios/arquitectura-semantica" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-gray-300 hover:text-white transition-colors py-1 cursor-pointer">
                      <i className="ri-code-box-line mr-2"></i>Arquitectura Semántica
                    </Link>
                    <Link to="/servicios/schema-markup-avanzado" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-gray-300 hover:text-white transition-colors py-1 cursor-pointer">
                      <i className="ri-file-code-line mr-2"></i>Schema Markup Avanzado
                    </Link>
                    <Link to="/servicios/seo-agentes-ia" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-gray-300 hover:text-white transition-colors py-1 cursor-pointer">
                      <i className="ri-robot-line mr-2"></i>SEO para Agentes IA
                    </Link>
                    <Link to="/servicios/voice-search-seo" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-gray-300 hover:text-white transition-colors py-1 cursor-pointer">
                      <i className="ri-mic-line mr-2"></i>Voice Search SEO
                    </Link>
                    <Link to="/servicios/progressive-web-app" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-gray-300 hover:text-white transition-colors py-1 cursor-pointer">
                      <i className="ri-smartphone-line mr-2"></i>Progressive Web App
                    </Link>
                    <Link to="/servicios/optimizacion-extrema" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-gray-300 hover:text-white transition-colors py-1 cursor-pointer">
                      <i className="ri-rocket-line mr-2"></i>Optimización Extrema
                    </Link>
                  </div>
                )}
              </div>
              
              <a href="#contacto" onClick={(e) => { handleContactClick(e); setMobileMenuOpen(false); }} className="px-6 py-3 bg-cyan-500 text-white rounded-lg font-medium text-center whitespace-nowrap cursor-pointer">Contactar</a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
